import { lighten, Stack, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from './pai-button';

interface Props {
  value: string;
  variant?: 'background' | 'white';
  readOnly?: boolean;
  selectOnFocus?: boolean;
  name?: string;
  rows?: number | null;
  placeholder?: string;
  label?: string;
  boldLabel?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  headAction?: React.ReactNode;
  style?: React.CSSProperties;
  /** @deprecated Legacy prop – kept for backward compat */
  disableBorder?: boolean;
  /** @deprecated Legacy prop – kept for backward compat */
  backgroundColor?: string;
  /** @deprecated Legacy prop – kept for backward compat */
  placeholderSize?: number;
  /** @deprecated Legacy prop – kept for backward compat */
  instruction?: string;
  /** @deprecated Legacy prop – kept for backward compat */
  optional?: boolean;

  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;

  onKeyUp?(): void;

  onKeyDown?(event?: React.KeyboardEvent): void;

  onPaste?(event: React.ClipboardEvent): void;

  /**
   * Called when the textarea loses focus. Useful for triggering auto-save logic.
   */
  onBlur?(): void;
}

const PaiTextArea: React.FC<Props> = ({
  value,
  variant = 'white',
  readOnly,
  selectOnFocus,
  rows,
  placeholder,
  label,
  boldLabel,
  disabled,
  error,
  errorText,
  headAction,
  onChange,
  onKeyUp,
  onKeyDown,
  onPaste,
  onBlur,
}) => {
  const { classes } = useStyles({ disabled, error, variant });
  const { t } = useTranslation();

  const [showAll, setShowAll] = useState(false);

  const defaultMinRows = 5;

  const toggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {!_.isNil(label) ? (
        <Typography className={classes.label} fontWeight={boldLabel ? 'bold' : 'normal'}>
          {label}
        </Typography>
      ) : null}
      <div className={classes.container}>
        {(value !== '' && rows !== 0) || !_.isNil(headAction) ? (
          <Stack
            direction={'row'}
            padding={'6px'}
            justifyContent={'space-between'}
            sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.greys.light}` })}
          >
            {!_.isNil(headAction) ? headAction : <div />}
            <PaiButton
              text={!showAll ? t('seeAll') : t('seeLess')}
              size={'extra-small'}
              border={'rounded'}
              disabled={typeof value !== 'string' || value.trim() === ''}
              action={toggleShowAll}
            />
          </Stack>
        ) : null}
        <TextField
          className={classes.textfield}
          InputProps={{
            readOnly: readOnly,
            disableUnderline: true,
            classes: { input: classes.fieldInput },
          }}
          variant={'standard'}
          fullWidth={true}
          placeholder={placeholder}
          multiline={true}
          {...(showAll ? { minRows: !_.isNil(rows) ? rows : defaultMinRows } : { rows: !_.isNil(rows) ? rows : defaultMinRows })}
          disabled={disabled}
          value={value}
          onChange={onChange}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={onBlur}
          onFocus={selectOnFocus ? handleFocus : undefined}
          error={!!error}
        />
      </div>
      {!!error && !_.isNil(errorText) ? <Typography className={classes.errorText}>{errorText}</Typography> : null}
    </div>
  );
};

const useStyles = makeStyles<{
  disabled?: boolean;
  error?: boolean;
  variant?: 'background' | 'white';
}>()((theme, { disabled, error, variant }) => ({
  container: {
    position: 'relative',
    background: !error ? (variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF') : lighten(theme.palette.error.main, 0.9),
    border:
      variant === 'background'
        ? undefined
        : !error
          ? disabled
            ? `1px solid ${theme.palette.action.disabled}`
            : `1px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.error.main}`,
    borderRadius: '10px',
  },
  textfield: {
    borderRadius: '10px',
    '& .MuiInput-root': {
      padding: '8px 4px',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
      height: '100%',
      top: 0,
      background: '#FFF',
    },
  },
  fieldInput: {
    fontSize: theme.typography.pxToRem(15),
    color: disabled ? theme.palette.greys.light : undefined,
    padding: '0 8px',
    borderRadius: '10px',
    '&::placeholder': {
      opacity: 0.5,
      fontSize: theme.typography.pxToRem(15),
    },
    '&:focus': {
      '&::placeholder': {
        opacity: 0,
      },
    },
  },
  errorText: {
    fontSize: theme.typography.pxToRem(13),
    marginTop: '6px',
    textAlign: 'right',
    color: theme.palette.error.main,
    padding: '0 6px',
  },
  label: {
    marginBottom: '4px',
    marginLeft: '4px',
    fontSize: theme.typography.pxToRem(14),
  },
  icon: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
  },
}));

export default PaiTextArea;
